import { type CSSProperties, type FC, memo } from "react";
import { token } from "@stubhub/broadway-foundation/tokens";
import { FanProtectFillIcon, VerifiedFillIcon } from "@stubhub/broadway-web/icons/functional";
import { MlbOnLightIcon } from "@stubhub/broadway-web/icons/partners";
import { Content } from "src/shared/components/content";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import {
  type PartnerIconDimensions,
  PerformerLabel,
  PerformerLabelIconType,
} from "src/website-apps/shared/components/hero-banner/PerformerLabel";
import {
  PerformerLabelType,
  useWillShowPerformerCredibilityLabel,
} from "src/website-apps/shared/hooks/useWillShowPerformerCredibilityLabel";

interface Props {
  className?: string | undefined;
  compactMlbLabel?: boolean;
  background?: Exclude<CSSProperties["background"], number>;
  color?: CSSProperties["color"];
  iconColor?: CSSProperties["color"];
  partnerIconDimensions?: PartnerIconDimensions | undefined;
  textStyle?: Pick<CSSProperties, "fontSize" | "fontStyle" | "fontWeight" | "lineHeight"> | undefined;
}

/**
 * Component to display a merchandizing label on performer pages, such as "MLB Authorized Ticket Marketplace", "official partner", or "Award winning artist"
 */
export const PerformerMerchLabel: FC<Props> = memo((props) => {
  const { className, compactMlbLabel = false, background, color, iconColor, partnerIconDimensions, textStyle } = props;
  const isStubhub = useIsStubhub();

  const labelType = useWillShowPerformerCredibilityLabel();

  if (labelType === PerformerLabelType.MLB) {
    return (
      <PerformerLabel
        startIcon={{ icon: MlbOnLightIcon, type: PerformerLabelIconType.PARTNER }}
        {...(compactMlbLabel
          ? {
              content: (
                <>
                  MLB <Content id="cid$#3316937#" />
                </>
              ),
            }
          : { contentId: "cid$#3789332#" as const })}
        background={background ?? token("colors.bg.surface.info")}
        color={color ?? token("colors.text")}
        {...(iconColor === undefined ? {} : { iconColor })}
        partnerIconDimensions={partnerIconDimensions}
        textStyle={textStyle}
        className={className}
      />
    );
  }

  if (labelType === PerformerLabelType.OFFICIAL_PARTNER) {
    return (
      <PerformerLabel
        startIcon={{ icon: VerifiedFillIcon, type: PerformerLabelIconType.STANDARD }}
        // "StubHub Official Partner" or "viagogo Official Partner"
        contentId={isStubhub ? "cid$#3850423#" : "cid$#3850422#"}
        {...(background === undefined ? {} : { background })}
        {...(color === undefined ? {} : { color })}
        {...(iconColor === undefined ? {} : { iconColor })}
        textStyle={textStyle}
        className={className}
      />
    );
  }

  if (labelType === PerformerLabelType.FAN_PROTECT_MOBILE) {
    return (
      <PerformerLabel
        startIcon={{ icon: FanProtectFillIcon, type: PerformerLabelIconType.STANDARD }}
        // "Every order is 100% guaranteed"
        contentId={"cid$#4043386#"}
        color={token("colors.text")}
        iconColor={token("colors.text.info")}
        background={token("colors.bg.surface.info")}
        textStyle={textStyle}
        className={className}
      />
    );
  }

  return null;
});
PerformerMerchLabel.displayName = "PerformerMerchLabel";
